/* ============================================================
   Lovely Colors — Interactions (Phase 4 polish)
   Vanilla port of design's `interactive.jsx`. Activated lazily;
   respects prefers-reduced-motion; cursor trail OFF by default.

   Hooks:
     - mouseTilt('.hero-stage', 18)       sets --tilt-x/y and --mouse-x/y on the target
     - spotlight('.lc-shop-card, .lb-card')  cursor-following radial spotlight
     - magnetic('.btn, .lc-cart-pill, .lc-hero-swatch', 0.15)  subtle attract on hover
     - scrollReveal('section')             adds .lc-reveal-on when in viewport
     - clickRipple                         emits a paint blob from click point
   ============================================================ */
(function () {
  'use strict';

  var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) return; // skip every interaction; respects user preference

  // Mark JS as active so CSS hides-then-reveals .lc-reveal elements and lazy images.
  // If this script never runs (failure or reduced-motion above), the class is absent and all content stays visible.
  document.documentElement.classList.add('lc-js');

  // ── mouse tilt ────────────────────────────────────────────
  function mouseTilt(selector, strength) {
    strength = strength || 12;
    var el = document.querySelector(selector);
    if (!el) return;
    function onMove(e) {
      var r = el.getBoundingClientRect();
      var nx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
      var ny = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
      nx = Math.max(-1, Math.min(1, nx));
      ny = Math.max(-1, Math.min(1, ny));
      el.style.setProperty('--tilt-y', (nx * strength) + 'deg');
      el.style.setProperty('--tilt-x', (-ny * strength * 0.6) + 'deg');
      el.style.setProperty('--mouse-x', nx);
      el.style.setProperty('--mouse-y', ny);
    }
    function reset() {
      el.style.setProperty('--tilt-y', '0deg');
      el.style.setProperty('--tilt-x', '0deg');
      el.style.setProperty('--mouse-x', '0');
      el.style.setProperty('--mouse-y', '0');
    }
    window.addEventListener('mousemove', onMove, { passive: true });
    el.addEventListener('mouseleave', reset);
  }

  // ── spotlight ─────────────────────────────────────────────
  function spotlight(selector) {
    function onMove(e) {
      document.querySelectorAll(selector).forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (e.clientX < r.left - 40 || e.clientX > r.right + 40 ||
            e.clientY < r.top  - 40 || e.clientY > r.bottom + 40) {
          el.style.setProperty('--spot-a', '0');
          return;
        }
        el.style.setProperty('--spot-x', (((e.clientX - r.left) / r.width) * 100) + '%');
        el.style.setProperty('--spot-y', (((e.clientY - r.top)  / r.height) * 100) + '%');
        el.style.setProperty('--spot-a', '1');
      });
    }
    window.addEventListener('mousemove', onMove, { passive: true });
  }

  // ── magnetic buttons ──────────────────────────────────────
  function magnetic(selector, strength) {
    strength = strength || 0.18;
    function setup(el) {
      if (el.dataset.magnetic === '1') return;
      el.dataset.magnetic = '1';
      function onMove(e) {
        var r = el.getBoundingClientRect();
        var dx = (e.clientX - (r.left + r.width / 2)) * strength;
        var dy = (e.clientY - (r.top + r.height / 2)) * strength;
        el.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
      }
      function leave() { el.style.transform = ''; }
      el.addEventListener('mousemove', onMove);
      el.addEventListener('mouseleave', leave);
    }
    function apply() {
      document.querySelectorAll(selector).forEach(setup);
    }
    apply();
    var mo = new MutationObserver(apply);
    mo.observe(document.body, { childList: true, subtree: true });
  }

  // ── scroll reveal ─────────────────────────────────────────
  function scrollReveal(selector) {
    var els = document.querySelectorAll(selector);
    els.forEach(function (el) { el.classList.add('lc-reveal'); });
    function revealAll() {
      document.querySelectorAll('.lc-reveal:not(.lc-revealed)').forEach(function (el) {
        el.classList.add('lc-revealed');
      });
    }
    if (!('IntersectionObserver' in window)) { revealAll(); return; }
    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('lc-revealed');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.08 });
    els.forEach(function (el) { obs.observe(el); });
    // fail-safe: never leave content invisible if the observer misses an element
    setTimeout(revealAll, 1600);
  }

  // ── lazy image fade-in (marks images .is-loaded so CSS can reveal them) ──
  function fadeImages() {
    function mark(img) { img.classList.add('is-loaded'); }
    function bind(img) {
      if (img.complete && img.naturalWidth > 0) { mark(img); return; }
      img.addEventListener('load', function () { mark(img); }, { once: true });
      img.addEventListener('error', function () { mark(img); }, { once: true });
    }
    document.querySelectorAll('img[loading="lazy"]').forEach(bind);
    // catch images added later (cart drawer, etc.)
    var mo = new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        m.addedNodes && m.addedNodes.forEach(function (n) {
          if (n.nodeType !== 1) return;
          if (n.tagName === 'IMG' && n.getAttribute('loading') === 'lazy') bind(n);
          if (n.querySelectorAll) n.querySelectorAll('img[loading="lazy"]').forEach(bind);
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
    // fail-safe: reveal any lazy image still unmarked after 2s
    setTimeout(function () {
      document.querySelectorAll('img[loading="lazy"]:not(.is-loaded)').forEach(mark);
    }, 2000);
  }

  // ── click ripple ──────────────────────────────────────────
  function clickRipple() {
    document.addEventListener('click', function (e) {
      var t = e.target.closest('.lc-hero-swatch, .lc-sim-palette button, .lc-shop-card .add, [data-shop-add]');
      if (!t) return;
      var color = (getComputedStyle(t).backgroundColor) || getComputedStyle(document.documentElement).getPropertyValue('--accent') || '#3FB5E6';
      var r = document.createElement('div');
      r.className = 'lc-click-ripple';
      r.style.left = e.clientX + 'px';
      r.style.top  = e.clientY + 'px';
      r.style.background = color;
      document.body.appendChild(r);
      setTimeout(function () { r.remove(); }, 900);
    });
  }

  // ── init when DOM is ready ────────────────────────────────
  function init() {
    mouseTilt('.hero-stage, .lc-hero-frame', 16);
    spotlight('.lc-shop-card, .lb-card, .lc-coffret');
    magnetic('.btn, .lc-cart-pill, .lc-hero-swatch', 0.15);
    scrollReveal('.lc-reveal, section, .lc-section, .products-strip, .lc-spaces-grid, .lc-sim-shell, .lc-journal-grid, .lc-contact-grid');
    fadeImages();
    clickRipple();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
